export const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MINUTES_PER_DAY = HOURS_PER_DAY * MINUTES_PER_HOUR;
const TIME_DIGITS = 2;

function wrapMinutes(totalMinutes: number): number {
  return ((totalMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
}

// "HH:MM" as minutes since midnight.
export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);

  return (hours ?? 0) * MINUTES_PER_HOUR + (minutes ?? 0);
}

// Minutes since midnight as "HH:MM"; values past either end of the day wrap around.
export function minutesToTime(totalMinutes: number): string {
  const wrapped = wrapMinutes(totalMinutes);
  const hours = String(Math.floor(wrapped / MINUTES_PER_HOUR)).padStart(TIME_DIGITS, '0');
  const minutes = String(wrapped % MINUTES_PER_HOUR).padStart(TIME_DIGITS, '0');

  return `${hours}:${minutes}`;
}

// "23:30" plus 60 minutes is "00:30".
export function addMinutesToTime(time: string, minutes: number): string {
  return minutesToTime(timeToMinutes(time) + minutes);
}

// Length of a time range; an end before its start belongs to the next day ("20:00" to "08:00" is 720).
export function durationInMinutes(startTime: string, endTime: string): number {
  return wrapMinutes(timeToMinutes(endTime) - timeToMinutes(startTime));
}

type MaybeTime = string | null | undefined;

// The end of a time range once its start moves from previousStart to newStart: the range keeps
// its duration, a range without an end yet lasts defaultMinutes, and an end set before any start
// is the user's and stays. Clearing the start leaves the end alone.
export function endTimeFollowingStart<End extends MaybeTime>(
  previousStart: MaybeTime,
  newStart: MaybeTime,
  currentEnd: End,
  defaultMinutes = MINUTES_PER_HOUR,
): string | End {
  if (!newStart) return currentEnd;
  if (!currentEnd) return addMinutesToTime(newStart, defaultMinutes);
  if (!previousStart) return currentEnd;

  return addMinutesToTime(newStart, durationInMinutes(previousStart, currentEnd));
}
